
import { useLanguage } from '../../../contexts/LanguageContext';
import { translations } from '../../../mocks/translations';

export default function ManagerSection() {
  const { language } = useLanguage();
  const t = translations[language].manager;

  return (
    <div className="space-y-8">
      <div className="text-center mb-12">
        <h2 className="text-4xl font-bold text-gray-900 mb-4">{t.title}</h2>
        <p className="text-lg text-gray-600">{t.subtitle}</p>
      </div>

      <div className="max-w-5xl mx-auto">
        {/* Manager Profile with Image */}
        <div className="bg-gradient-to-br from-white to-teal-50 p-8 lg:p-10 rounded-xl border border-teal-200 shadow-lg mb-8">
          <div className="flex flex-col lg:flex-row items-center gap-8">
            <div className="w-48 h-48 lg:w-56 lg:h-56 rounded-2xl overflow-hidden shadow-xl flex-shrink-0">
              <img 
                src="https://static.readdy.ai/image/fa61325f1bcb10e63a715068dd996489/1c5bb111b9dcf9a6ace7a11900ea5622.jpeg"
                alt="Manager Portrait"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="flex-1 text-center lg:text-left">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-teal-100 rounded-full mb-3">
                <i className="ri-star-line text-sm text-teal-700"></i>
                <span className="text-xs font-medium text-teal-800">{t.badge}</span>
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-2">{t.name}</h3>
              <p className="text-lg text-gray-600 mb-4">{t.role}</p>
              <p className="text-sm text-gray-700 leading-relaxed">
                {t.description}
              </p>
            </div>
          </div>
        </div>

        {/* Leadership Cards with Images */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
            <div className="h-36 relative">
              <img 
                src="https://public.readdy.ai/ai/img_res/242eb44315018ce238e5e6d52bae83b2.jpg"
                alt="Leadership"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-lg flex-shrink-0">
                  <i className="ri-briefcase-line text-xl text-teal-600"></i>
                </div>
                <div>
                  <h4 className="text-lg font-bold text-gray-900 mb-2">{t.leadershipTitle}</h4>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {t.leadershipDescription}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
            <div className="h-36 relative">
              <img 
                src="https://readdy.ai/api/search-image?query=Diverse%20professional%20team%20collaboration%20in%20modern%20office%2C%20business%20people%20working%20together%2C%20teamwork%20and%20cooperation%20concept%2C%20bright%20contemporary%20workspace%2C%20positive%20atmosphere&width=500&height=200&seq=manager3&orientation=landscape"
                alt="Team"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-emerald-100 rounded-lg flex-shrink-0">
                  <i className="ri-team-line text-xl text-emerald-600"></i>
                </div>
                <div>
                  <h4 className="text-lg font-bold text-gray-900 mb-2">{t.teamTitle}</h4>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {t.teamDescription}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
            <div className="h-36 relative">
              <img 
                src="https://readdy.ai/api/search-image?query=Business%20growth%20chart%20on%20digital%20screen%20with%20upward%20trend%2C%20financial%20success%20and%20progress%2C%20modern%20data%20visualization%2C%20teal%20and%20green%20color%20scheme%2C%20professional%20corporate%20setting&width=500&height=200&seq=manager4&orientation=landscape"
                alt="Growth"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-lg flex-shrink-0">
                  <i className="ri-line-chart-line text-xl text-teal-600"></i>
                </div>
                <div>
                  <h4 className="text-lg font-bold text-gray-900 mb-2">{t.growthTitle}</h4>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {t.growthDescription}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
            <div className="h-36 relative">
              <img 
                src="https://readdy.ai/api/search-image?query=Legal%20compliance%20and%20regulatory%20framework%20concept%2C%20official%20documents%20with%20stamps%20and%20seals%2C%20professional%20legal%20office%20setting%2C%20organized%20paperwork%2C%20trustworthy%20business%20environment&width=500&height=200&seq=manager5&orientation=landscape"
                alt="Compliance"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-emerald-100 rounded-lg flex-shrink-0">
                  <i className="ri-shield-check-line text-xl text-emerald-600"></i>
                </div>
                <div>
                  <h4 className="text-lg font-bold text-gray-900 mb-2">{t.complianceTitle}</h4>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {t.complianceDescription}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
